import { HttpError, KubernetesObject } from '@kubernetes/client-node'
import * as yaml from 'js-yaml'
import { CheckableWorkloadType, ExecuteContext, IApplicationModel, KV, PlanContext, PlanContextData, PluginInfoList } from 'yam-types'
import { LOCK_MSG } from '../constants'
import YamApplyContext from '../context/apply'
import YamPlanContext from '../context/plan'
import KubernetesOperatorClient from '../kube/kube-client'
import TemplateRender from '../template/render'
import type { EngineConfig, KubernetesEnvironment, OperatorParam } from '../types'
import { createLogger, generateEnvSuffix, generateWorkloadNameWithEnv, sleep, validateJsonSchema } from '../util'
import { MergedHandler } from './composer'
import YamStateLocker from './locker'
import YamStateStore, { YamMetadata } from './store'

const FIRST_CHECK_WAIT_MILLS = 5000

export const COPIED_APP_MODEL_FIELD = '_currentModelFull_'
/**
 * YAM engine core, implement Plan & Apply process
 */
export default class YamEngine {

  private templateRender: TemplateRender
  private operatorParam: OperatorParam
  private store: YamStateStore
  private locker: YamStateLocker
  private kubeClient: KubernetesOperatorClient

  private log = createLogger("yam-engine")

  constructor(templateRender: TemplateRender, operatorParam: OperatorParam, kubeClient: KubernetesOperatorClient) {
    this.templateRender = templateRender
    this.operatorParam = operatorParam
    this.kubeClient = kubeClient
    this.store = new YamStateStore(operatorParam)
    this.locker = new YamStateLocker(operatorParam, kubeClient)
  }

  public initPlanContext(prev: IApplicationModel, current: IApplicationModel, pluginInfoList: PluginInfoList, cluster: KubernetesEnvironment, customizedValues: KV, engineSettings?: EngineConfig, multiEnvMode?: boolean): YamPlanContext {
    if (multiEnvMode) {
      this.log.info('engine runs in multiEnvMode, env suffix will append to applied resources')
    }
    // create Plan stage context instance
    const workloadName = multiEnvMode ? generateWorkloadNameWithEnv(current.metadata.app, cluster.name) : current.metadata.app
    const planContextData: PlanContextData = {
      // initialize apply stage actions
      actions: [],
      // provide complete application model data
      currentModelFull: current,
      previousModelFull: prev,
      // dynamic parameters for template rendering
      customizedValues,
      appendParamLabels: engineSettings?.clientConf.appendParamLabels ?? {},
      // others essential context data
      workingDir: this.operatorParam.workingDir,
      stackName: cluster.stack,
      envName: cluster.name,
      envLocators: cluster.envLocators,

      runMode: this.operatorParam.runMode,
      kubeContextName: cluster.kubeConfContextName,
      kubeConfigPath: cluster.kubeConfPath,

      dryRun: this.operatorParam.dryRunMode || false,
      forceRestartWorkload: this.operatorParam.forceRestartWorkload,
      templateOutput: this.operatorParam.templateOutput,
      multiEnvMode,

      app: current.metadata.app,
      namespace: current.metadata.namespace,
      workloadName,
      serviceAccountName: 'sa-' + current.metadata.namespace + '-' + current.metadata.app + '-' + generateEnvSuffix(cluster.name),

      pluginInfoList
    }
    // hidden logic: clone a model that being stored after applying, to keep it immutable during CD process
    const tmpContext = planContextData as unknown as KV
    tmpContext[COPIED_APP_MODEL_FIELD] = JSON.parse(JSON.stringify(planContextData.currentModelFull))
    return new YamPlanContext(this.templateRender, planContextData)
  }

  /**
   * Plan Stage, calculate diff and apply each operation handler of plugins if JSON path matched
   */
  public async plan(planContext: YamPlanContext, jsonSchema: unknown, handlers: MergedHandler[]): Promise<YamPlanContext> {
    this.log.info('============== Plan Stage Start ==============')
    // YAM data validation with final json schema
    if (planContext.data.partialPluginMode) {
      this.log.info(`JSON schema validation skipped.`)
    } else {
      const errors = validateJsonSchema(planContext.data.currentModelFull, jsonSchema)
      if (errors) {
        this.log.error(errors, "schema check didn't pass")
        throw new Error('application model is not valid.')
      } else {
        this.log.info('schema check passed, application model definition is valid.')
      }
    }

    // query by each JSON path defined in plugins, call corresponding operate functions
    await planContext.diffJsonPathAndInvokeHandlers(handlers)

    // store the planned binary file to local or/and cloud
    await this.store.persistPlan(planContext as PlanContext, this.operatorParam, this.templateRender)
    this.log.info('============== Plan Stage End ================\n\n')
    return planContext
  }

  /**
   * Apply Stage, lock and call actions and apply kubernetes objects
   */
  public async apply(param: OperatorParam, planCtx: PlanContext, executeCtx: ExecuteContext): Promise<void> {
    this.log.info('============== Apply Stage Start =============')
    const partialChangeMode = param.filteredPlugins && param.filteredPlugins.length > 0
    try {
      await this.locker.lock(planCtx)
      for (const action of planCtx.data.actions) {
        const actionName = (action as unknown as KV).actionName || action.name || '<anonymous>'
        const start = Date.now()
        this.log.info(`[${actionName}] - action executing...`)
        await action(executeCtx)
        this.log.info(`[${actionName}] - action done, cost: ${Date.now() - start}ms\n`)
      }

      // execute and clean patch queue, then apply managed resources into k8s
      await (executeCtx as YamApplyContext).applyPatchesAndManagedResources()

      // check workload status
      const workload = planCtx.data.currentModelFull.deploy
      if (workload && param.statusWaitSeconds > 0) {
        if (partialChangeMode) {
          executeCtx.log.info(`partial apply mode detected, only selected plugins are handled, will not check workload or store results for: ${planCtx.data.workloadName}`)
        } else {
          await this.store.storeYamMetadata(planCtx, executeCtx, this.templateRender.fileContentCache, false)
          await sleep(FIRST_CHECK_WAIT_MILLS)
          executeCtx.log.info(`start checking workload status util ready, max check time is: ${param.statusWaitSeconds}s`)
          await executeCtx.k8sClient.checkWorkloadStatus((workload as KV).type as CheckableWorkloadType, planCtx.data.workloadName, param.statusWaitSeconds * 1000)
        }
      }
      if (!partialChangeMode) {
        await this.store.storeYamMetadata(planCtx, executeCtx, this.templateRender.fileContentCache, true)
      }
      await this.locker.unlock(planCtx)
      this.log.info('============== Apply Stage End ===============')
    } catch (e) {
      if (e instanceof HttpError) {
        this.log.error(`k8s client - ${e.message}: ${e.statusCode}\n${yaml.dump(e.body)}`)
      }
      if (!partialChangeMode) {
        await this.store.storeYamMetadata(planCtx, executeCtx, this.templateRender.fileContentCache, false, e)
      }
      if (e.message?.indexOf(LOCK_MSG) === -1) {
        await this.locker.unlock(planCtx)
      }
      throw e
    }
  }

  public async uninstall(yamMetadata: YamMetadata, handlers: MergedHandler[], pluginInfoList: PluginInfoList, cluster: KubernetesEnvironment): Promise<void> {
    const appModel = yamMetadata.appModel
    const planContext = this.initPlanContext({ metadata: appModel.metadata }, appModel, pluginInfoList, cluster, {})
    // query by each JSON path defined in plugins, call corresponding 'uninstaller' functions
    await planContext.diffJsonPathAndInvokeHandlers(handlers)
    const executeCtx = new YamApplyContext(this.kubeClient, !!this.operatorParam.dryRunMode, this.operatorParam.forceRestartWorkload)
    for (const action of planContext.data.actions) {
      const actionName = (action as unknown as KV).actionName || action.name || '<anonymous>'
      const start = Date.now()
      this.log.info(`[${actionName}] - uninstall action executing...`)
      await action(executeCtx)
      this.log.info(`[${actionName}] - uninstall action done, cost: ${Date.now() - start}ms\n`)
    }

    for (const obj of yamMetadata.appliedResources) {
      if (obj instanceof Array) {
        for (const subObj of obj) {
          await this.deleteUninstalledResources(subObj)
        }
      } else {
        await this.deleteUninstalledResources(obj)
      }
    }
  }

  private async deleteUninstalledResources(obj: KubernetesObject) {
    if (obj.kind === 'CustomResourceDefinition') {
      this.log.warn(`CRD: '${obj.metadata?.name}' will not be deleted, if you want to remove CRD, please delete it manually.`)
    } else {
      await this.kubeClient.delete({
        name: obj.metadata?.name,
        kind: obj.kind || '<unknown>',
        namespace: obj.metadata?.namespace
      })
    }
  }
}
